import { useMemo, useCallback } from "react";
import { IPMAStation } from "@/types/ipma-station/ipma-station.types";
import { FilterOption } from "@/components/ui/table-header/types";

interface UseIPMAStationFiltersProps {
  stations: IPMAStation[];
  searchTerm: string;
  showArchived: boolean;
  basinFilters: string[];
  basins?: { id: string; name: string }[];
}

export const useIPMAStationFilters = ({
  stations,
  searchTerm,
  showArchived,
  basinFilters,
  basins = [],
}: UseIPMAStationFiltersProps) => {

  const normalizeText = useCallback((text: string = "") => {
    return text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }, []);

  // Create a map of basin ID to basin name for quick lookup
  const basinMap = useMemo(() => {
    const map: Record<string, string> = {};
    basins.forEach((basin) => {
      map[basin.id] = basin.name;
    });
    return map;
  }, [basins]);

  const filteredStations = useMemo(() => {
    if (!stations) return [];

    return stations.filter((station) => {
      // Filter by archive status - show either archived or non-archived stations
      if (showArchived) {
        if (!station.isArchived) return false;
      } else {
        if (station.isArchived) return false;
      }

      // Filter by basin - basinFilters contains basin names, so we need to check if the station's basin name is in the filters
      if (basinFilters.length > 0) {
        const stationBasinName = basinMap[station.basinId];
        if (!stationBasinName || !basinFilters.includes(stationBasinName)) {
          return false;
        }
      }

      // Filter by search term
      if (searchTerm) {
        const term = normalizeText(searchTerm);
        const normalizedName = normalizeText(station.name);
        const stationBasinName = basinMap[station.basinId] || "";
        const normalizedBasin = normalizeText(stationBasinName);

        return normalizedName.includes(term) || normalizedBasin.includes(term);
      }

      return true;
    });
  }, [stations, searchTerm, showArchived, basinFilters, normalizeText, basinMap]);

  const getUniqueOptions = useCallback(
    (field: "basin"): FilterOption[] => {
      if (!stations || field !== "basin") return [];

      // Filter stations by all criteria except basin (since we're generating basin options)
      const filteredByOtherFilters = stations.filter((station) => {
        // Filter by archive status
        if (showArchived) {
          if (!station.isArchived) return false;
        } else {
          if (station.isArchived) return false;
        }

        // Filter by search term
        if (searchTerm) {
          const term = normalizeText(searchTerm);
          const normalizedName = normalizeText(station.name);
          const stationBasinName = basinMap[station.basinId] || "";
          const normalizedBasin = normalizeText(stationBasinName);

          if (!normalizedName.includes(term) && !normalizedBasin.includes(term)) {
            return false;
          }
        }

        return true;
      });

      // Extract unique basin names from the filtered stations
      const uniqueBasinNames = new Set<string>();

      filteredByOtherFilters.forEach((station) => {
        const basinName = basinMap[station.basinId];
        if (basinName) {
          uniqueBasinNames.add(basinName);
        }
      });

      return Array.from(uniqueBasinNames)
        .sort()
        .map((basinName) => ({
          label: basinName,
          value: basinName,
        }));
    },
    [stations, searchTerm, showArchived, normalizeText, basinMap]
  );

  return {
    filteredStations,
    getUniqueOptions,
  };
}; 